import axios, { AxiosResponse } from 'axios'
import { API_URL } from '../../../../../../constants'
import { ApiResponse } from '../../../../../common/api/response.types'
import { DASHBOARD_API } from './consts'
import {
  DashboardSnapshot,
  DrilldownResponse,
  Granularity,
  MerchantsResponse,
  ShortfallOutcome,
  ShortfallTimeseriesResponse,
  TimeseriesResponse,
} from './types'

/**
 * In-flight requests keyed by URL+params (CPAYMENT-1689).
 *
 * The dashboard's blocks each load themselves and the page re-renders on every
 * poll, so a re-mount or a re-render racing an in-flight load used to put two
 * identical GETs on the wire at once. On endpoints that take seconds and are
 * capped by a 10 s gateway timeout that is not merely wasteful — the duplicate
 * competes with the request it duplicates. Callers sharing an in-flight promise
 * cannot produce that pair regardless of what triggered the second call.
 */
const inFlight = new Map<string, Promise<unknown>>()

export function dedupe<T>(key: string, run: () => Promise<T>): Promise<T> {
  const existing = inFlight.get(key)
  if (existing) return existing as Promise<T>

  const promise = run().finally(() => inFlight.delete(key))
  inFlight.set(key, promise)
  return promise
}

export const getDashboard = () =>
  dedupe<AxiosResponse<ApiResponse<DashboardSnapshot>>>('snapshot', () =>
    axios.get<ApiResponse<DashboardSnapshot>>(`${API_URL}${DASHBOARD_API}`)
  )

export const getTimeseries = (granularity: Granularity, currency: string) =>
  dedupe<AxiosResponse<ApiResponse<TimeseriesResponse>>>(`timeseries:${granularity}:${currency}`, () =>
    axios.get<ApiResponse<TimeseriesResponse>>(`${API_URL}${DASHBOARD_API}/timeseries`, {
      params: { granularity, currency },
    })
  )

/**
 * Отчёт непокрытого спроса. merchant / paymentMethod — внутренние id, оба
 * необязательны и комбинируются по AND; отсутствуют — отчёт по всем shared.
 * outcome переключает цифру выплат, депозитную серию не трогает; 'all' не
 * отправляется — режим по умолчанию задаёт мэтчинг.
 * Срез входит в ключ дедупликации: иначе смена фильтра вернула бы промис
 * предыдущего среза.
 */
export const getShortfallTimeseries = (
  currency: string,
  filter?: { merchant?: number; paymentMethod?: number; outcome?: ShortfallOutcome }
) =>
  dedupe<AxiosResponse<ApiResponse<ShortfallTimeseriesResponse>>>(
    `shortfall:${currency}:${filter?.merchant ?? ''}:${filter?.paymentMethod ?? ''}:${
      filter?.outcome ?? 'all'
    }`,
    () =>
      axios.get<ApiResponse<ShortfallTimeseriesResponse>>(
        `${API_URL}${DASHBOARD_API}/shortfall-timeseries`,
        {
          params: {
            currency,
            granularity: 'hour',
            ...(filter?.merchant ? { merchant: filter.merchant } : {}),
            ...(filter?.paymentMethod ? { paymentMethod: filter.paymentMethod } : {}),
            ...(filter?.outcome && filter.outcome !== 'all' ? { outcome: filter.outcome } : {}),
          },
        }
      )
  )

export const getMerchants = (currency: string) =>
  dedupe<AxiosResponse<ApiResponse<MerchantsResponse>>>(`merchants:${currency}`, () =>
    axios.get<ApiResponse<MerchantsResponse>>(`${API_URL}${DASHBOARD_API}/merchants`, {
      params: { currency },
    })
  )

// Drill-down is opened by hand from a modal, so it is not deduped: repeating the
// same click must really refetch.
export const getDrilldown = (signal: string, params?: { currency?: string; merchant?: number }) =>
  axios.get<ApiResponse<DrilldownResponse>>(`${API_URL}${DASHBOARD_API}/drilldown/${signal}`, {
    params,
  })
